import React, {useState} from 'react';
import {Button, Icon, Loader} from 'semantic-ui-react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {getFreshIdToken} from '../../utils/authUtil';
import {submitComment, addNewInlineCommentPlaceholder} from '../../modules/comment';

const REACTIONS = [
  {key: 'LIKE', codePoint: 0x1f44d, className: ''},
  {key: 'LOVE', codePoint: 0x2764, className: 'reaction-heart'},
  {key: 'LAUGH', codePoint: 0x1f602, className: ''},
  {key: 'SURPRISE', codePoint: 0x1f62e, className: ''},
  {key: 'SAD', codePoint: 0x1f625, className: ''},
  {key: 'ANGRY', codePoint: 0x1f621, className: ''}
];

/**
 * Floating selection toolbar shown when the reader selects text in a
 * 'tiptap'-format book. It is the scene-aware analog of
 * components/textEditor/reactionsComponent.js: a comment button that opens a
 * placeholder in the sidebar, plus one-tap reaction buttons. Both build the
 * anchor as {sceneId, startKey, startOffset, endKey, endOffset} (startKey/endKey
 * are pids), exactly the inlineInfo shape the InlineComment model stores.
 */
const SceneReactionsToolbar = ({sceneId, anchor, coords, onClose}) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const userProfile = useSelector(state => state.user.userProfile);
  const currentBook = useSelector(state => state.book.currentBook);
  const currentSpineIndex = useSelector(
    state => state.readerApp.currentSpineIndex
  );

  const [submitting, setSubmitting] = useState(false);

  const inlineInfo = {
    sceneId,
    startKey: anchor.startKey,
    startOffset: anchor.startOffset,
    endKey: anchor.endKey,
    endOffset: anchor.endOffset
  };

  // A Draft-SelectionState-like adapter so we can reuse the same
  // addNewInlineCommentPlaceholder code path the Draft reader uses.
  const selectionLike = {
    getStartKey: () => anchor.startKey,
    getStartOffset: () => anchor.startOffset,
    getEndKey: () => anchor.endKey,
    getEndOffset: () => anchor.endOffset,
    getSceneId: () => sceneId
  };

  const onCommentClick = event => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(
      addNewInlineCommentPlaceholder(
        coords.x,
        coords.y,
        selectionLike,
        userProfile
      )
    );
    onClose && onClose();
  };

  const onReactionClick = async (event, reaction) => {
    event.preventDefault();
    event.stopPropagation();
    setSubmitting(reaction);
    const idToken = await getFreshIdToken();
    const currentChapter = currentBook.parts[currentSpineIndex];
    if (currentChapter === undefined) {
      setSubmitting(false);
      return;
    }
    dispatch(
      submitComment(
        idToken,
        currentBook._id,
        currentChapter._id,
        null,
        {reaction, kind: 'inline', inlineInfo},
        () => {
          setSubmitting(false);
          if (document.activeElement !== document.body) {
            document.activeElement.blur();
          }
          onClose && onClose();
        }
      )
    );
  };

  const preventBlur = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      className="scene-selection-toolbar"
      onMouseDown={preventBlur}
      style={{
        position: 'fixed',
        top: Math.max(coords.top - 44, 8),
        left: coords.left,
        zIndex: 1000
      }}
    >
      <Button.Group size="small">
        <Button icon onClick={onCommentClick}>
          <Icon name="comment" flipped="horizontally" />
        </Button>
        {REACTIONS.map(reaction => (
          <Button
            key={reaction.key}
            icon
            onClick={e => onReactionClick(e, reaction.key)}
          >
            {submitting === reaction.key ? (
              <Loader active inline size="tiny" />
            ) : (
              <span
                role="img"
                aria-label={reaction.key}
                className={reaction.className}
              >
                {String.fromCodePoint(reaction.codePoint)}
              </span>
            )}
          </Button>
        ))}
      </Button.Group>
    </div>
  );
};

export default SceneReactionsToolbar;
